import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TimelineButtons } from '../components/TimelineButtons';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Plus, Trash2, Save, FlaskConical, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import AttachmentExpander from '../components/AttachmentExpander';
import useAttachmentCounts from '../hooks/useAttachmentCounts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LabTracker() {
    const { token } = useAuth();
    const [entries, setEntries] = useState([]);
    const [commonLabs, setCommonLabs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddLab, setShowAddLab] = useState(false);
    const [selectedTest, setSelectedTest] = useState(null);
    const [testHistory, setTestHistory] = useState([]);

    // Attachment counts for lab history items
    const labCounts = useAttachmentCounts('lab_result', testHistory.map(e => e.id));
    
    // New lab entry state
    const [testName, setTestName] = useState('');
    const [testValue, setTestValue] = useState('');
    const [testUnit, setTestUnit] = useState('');
    const [referenceRange, setReferenceRange] = useState('');
    const [testDate, setTestDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [testNotes, setTestNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchCommonLabs = async () => {
            try {
                const res = await fetch(`${API_URL}/api/labs/common`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setCommonLabs(data.labs || []);
                }
            } catch (error) {
                console.error('Failed to fetch common labs:', error);
            }
        };
        fetchCommonLabs();
    }, [token]);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/labs?limit=50`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setEntries(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch entries:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const fetchTestHistory = async (test) => {
        try {
            const res = await fetch(`${API_URL}/api/labs/test/${encodeURIComponent(test)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setTestHistory(data);
                setSelectedTest(test);
            }
        } catch (error) {
            console.error('Failed to fetch test history:', error);
        }
    };

    const saveLab = async () => {
        if (!testName.trim() || !testValue.trim()) {
            toast.error('Please enter test name and value');
            return;
        }

        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/labs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    date: testDate,
                    test_name: testName,
                    value: testValue,
                    unit: testUnit || null,
                    reference_range: referenceRange || null,
                    notes: testNotes || null
                })
            });

            if (res.ok) {
                toast.success('Lab result saved');
                setShowAddLab(false);
                setTestName('');
                setTestValue('');
                setTestUnit('');
                setReferenceRange('');
                setTestNotes('');
                fetchEntries();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save lab result');
        } finally {
            setSaving(false);
        }
    };

    const deleteLab = async (entryId) => {
        if (!window.confirm('Delete this lab result?')) return;

        try {
            const res = await fetch(`${API_URL}/api/labs/${entryId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Lab result deleted');
                fetchEntries();
                if (selectedTest) {
                    fetchTestHistory(selectedTest);
                }
            }
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    // Group entries by test name
    const groupedEntries = entries.reduce((acc, entry) => {
        if (!acc[entry.test_name]) {
            acc[entry.test_name] = [];
        }
        acc[entry.test_name].push(entry);
        return acc;
    }, {});

    const getTrend = (history) => {
        if (history.length < 2) return null;
        const latest = parseFloat(history[0].value);
        const previous = parseFloat(history[1].value);
        if (isNaN(latest) || isNaN(previous)) return null;
        if (latest > previous) return 'up';
        if (latest < previous) return 'down';
        return 'stable';
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Lab Tracker</h1>
                    <p className="text-muted-foreground mt-1">Track your autoimmune markers and lab results</p>
                </div>
                <div className="flex items-center gap-3">
                    <TimelineButtons />
                    <Button onClick={() => setShowAddLab(true)} data-testid="add-lab-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Lab Result
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Add Lab Form */}
                    {showAddLab && (
                        <Card className="border-2 border-indigo-200 dark:border-indigo-800">
                            <CardHeader>
                                <CardTitle>Add Lab Result</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Test Name</label>
                                    <Select value={testName} onValueChange={setTestName}>
                                        <SelectTrigger data-testid="test-name-select">
                                            <SelectValue placeholder="Select or type test name" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {commonLabs.map((lab) => (
                                                <SelectItem key={lab.name} value={lab.name}>
                                                    {lab.name} - {lab.description}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        placeholder="Or type custom test name"
                                        value={testName}
                                        onChange={(e) => setTestName(e.target.value)}
                                        className="mt-2"
                                        data-testid="test-name-input"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Value</label>
                                        <Input
                                            placeholder="e.g., 5.2"
                                            value={testValue}
                                            onChange={(e) => setTestValue(e.target.value)}
                                            data-testid="test-value-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Unit</label>
                                        <Input
                                            placeholder="e.g., mg/dL"
                                            value={testUnit}
                                            onChange={(e) => setTestUnit(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Reference Range</label>
                                        <Input
                                            placeholder="e.g., 0.5-4.5"
                                            value={referenceRange}
                                            onChange={(e) => setReferenceRange(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Test Date</label>
                                        <Input
                                            type="date"
                                            value={testDate}
                                            onChange={(e) => setTestDate(e.target.value)}
                                            data-testid="test-date-input"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Notes</label>
                                    <Textarea
                                        placeholder="Any notes about this result"
                                        value={testNotes}
                                        onChange={(e) => setTestNotes(e.target.value)}
                                        rows={2}
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <Button onClick={saveLab} disabled={saving} className="flex-1" data-testid="save-lab-btn">
                                        <Save className="w-4 h-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Result'}
                                    </Button>
                                    <Button variant="outline" onClick={() => setShowAddLab(false)}>Cancel</Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Lab Results by Test */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Your Lab Results</CardTitle>
                            <CardDescription>Click on a test to view history</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : Object.keys(groupedEntries).length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FlaskConical className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No lab results recorded yet</p>
                                    <Button variant="link" onClick={() => setShowAddLab(true)}>Add your first result</Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {Object.entries(groupedEntries).map(([test, history]) => {
                                        const latest = history[0];
                                        const trend = getTrend(history);
                                        return (
                                            <div
                                                key={test}
                                                className="p-4 rounded-xl border bg-muted/30 cursor-pointer hover:shadow-md transition-shadow"
                                                onClick={() => fetchTestHistory(test)}
                                                data-testid={`lab-card-${test.toLowerCase().replace(/\s+/g, '-')}`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium">{test}</span>
                                                    {trend && (
                                                        <div className={`flex items-center gap-1 ${
                                                            trend === 'up' ? 'text-red-500' : 
                                                            trend === 'down' ? 'text-emerald-500' : 'text-amber-500'
                                                        }`}>
                                                            {trend === 'up' && <TrendingUp className="w-4 h-4" />}
                                                            {trend === 'down' && <TrendingDown className="w-4 h-4" />}
                                                            {trend === 'stable' && <Minus className="w-4 h-4" />}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-2xl font-bold">{latest.value}</span>
                                                    {latest.unit && <span className="text-sm text-muted-foreground">{latest.unit}</span>}
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {format(parseISO(latest.date), 'MMM d, yyyy')}
                                                    {latest.reference_range && ` • Ref: ${latest.reference_range}`}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    {/* Test History */}
                    {selectedTest && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{selectedTest} History</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {testHistory.map((entry) => (
                                        <div key={entry.id} className="p-3 rounded-lg border bg-muted/30">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="font-bold">{entry.value}</span>
                                                    {entry.unit && <span className="text-sm ml-1">{entry.unit}</span>}
                                                </div>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={(e) => { e.stopPropagation(); deleteLab(entry.id); }}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <p className="text-xs text-muted-foreground">{format(parseISO(entry.date), 'MMM d, yyyy')}</p>
                                            {entry.notes && <p className="text-xs mt-1">{entry.notes}</p>}
                                            <AttachmentExpander
                                                parentType="lab_result"
                                                parentId={entry.id}
                                                initialCount={labCounts[`lab_result:${entry.id}`] || 0}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Common Labs Reference */}
                    <Card className="bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-950/30 dark:to-indigo-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg">🔬 Common Autoimmune Labs</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 text-sm">
                                {commonLabs.slice(0, 8).map((lab) => (
                                    <div key={lab.name} className="flex justify-between items-center">
                                        <span className="font-medium">{lab.name}</span>
                                        <span className="text-xs text-muted-foreground truncate max-w-[120px]">{lab.description}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg">💡 Lab Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Track trends over time, not single values</p>
                            <p>• Note fasting status when relevant</p>
                            <p>• Include reference ranges from your lab</p>
                            <p>• Discuss changes with your doctor</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
